import { useState, useEffect, useMemo } from 'react'
import { DateTime } from 'luxon'
import ApiClient from '@/services/ApiClient'
import { notify } from '@/utils/notification'

/**
 * Custom hook for Calendar component
 * Manages calendar state, API integration, and date logic
 *
 * @returns {Object} Calendar state and methods
 */
const useCalendar = () => {
  // API Data
  const [availabilityData, setAvailabilityData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Calendar State
  const [currentMonth, setCurrentMonth] = useState(DateTime.now())
  const [selectedDate, setSelectedDate] = useState(null)

  /**
   * Generate calendar grid for a given month
   * Creates 42 cells (6 weeks × 7 days) to ensure consistent height
   *
   * @param {DateTime} monthDateTime - Luxon DateTime for the month
   * @returns {DateTime[]} Array of 42 DateTime objects
   */
  const generateCalendarGrid = (monthDateTime) => {
    // Get first day of the month
    const firstDayOfMonth = monthDateTime.startOf('month')

    // Get day of week (0 = Sunday, 6 = Saturday)
    // Luxon weekday: 1=Monday, 7=Sunday, so we convert to 0=Sunday format
    const startDayOfWeek = firstDayOfMonth.weekday % 7

    // Calculate start of calendar grid (may include previous month)
    const calendarStart = firstDayOfMonth.minus({ days: startDayOfWeek })

    // Generate 42 cells (6 weeks × 7 days)
    const dates = []
    for (let i = 0; i < 42; i++) {
      dates.push(calendarStart.plus({ days: i }))
    }

    return dates
  }

  // Fetch availability from API
  const fetchAvailability = async () => {
    try {
      setLoading(true)
      setError(null)

      const apiClient = ApiClient.getInstance()
      const data = await apiClient.get('/availability/', {
        notification: {
          showNotification: false // Silent request - no success toast
        }
      })

      setAvailabilityData(data)

      // Set currentMonth based on first available date
      if (data.dates && data.dates.length > 0) {
        const firstDate = DateTime.fromISO(data.dates[0].date)
        setCurrentMonth(firstDate.startOf('month'))
      } else {
        // No available dates
        notify.info('No available consultation slots in the next 14 days')
      }
    } catch (err) {
      setError(err.message || 'Failed to load availability')
      notify.error('Failed to load available dates. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Auto-fetch on mount
  useEffect(() => {
    fetchAvailability()
  }, [])

  // Derived: Set of available dates for O(1) lookup
  const availableDates = useMemo(() => {
    if (!availabilityData || !availabilityData.dates) return new Set()
    return new Set(availabilityData.dates.map(d => d.date))
  }, [availabilityData])

  // Derived: Map of date to slots for quick slot access
  const slotsMap = useMemo(() => {
    if (!availabilityData || !availabilityData.dates) return new Map()
    return new Map(
      availabilityData.dates.map(dateObj => [dateObj.date, dateObj.slots])
    )
  }, [availabilityData])

  // Derived: Calendar grid (42 cells = 6 weeks)
  const calendarDates = useMemo(() => {
    return generateCalendarGrid(currentMonth)
  }, [currentMonth])

  // Helper: Check if date is available
  const isDateAvailable = (dateStr) => availableDates.has(dateStr)

  // Helper: Check if date is today
  const isDateToday = (date) => {
    const today = DateTime.now().startOf('day')
    return date.hasSame(today, 'day')
  }

  // Helper: Check if date is in current month
  const isDateInCurrentMonth = (date) => {
    return date.hasSame(currentMonth, 'month')
  }

  return {
    // Data
    availabilityData,
    availableDates,
    slotsMap,
    calendarDates,
    currentMonth,
    selectedDate,
    loading,
    error,

    // Actions
    fetchAvailability,
    setSelectedDate,
    isDateAvailable,
    isDateToday,
    isDateInCurrentMonth
  }
}

export default useCalendar
